import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { importLeadsFromExcel } from "../../Services/SmartFloApis";

export default function ImportLeadsExcelModal({ open, onClose, manager, onSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setFile(selected);
      setError(null);
    }
    // reset input so same file can be re-selected after clearing
    e.target.value = "";
  };

  const handleClose = () => {
    if (loading) return;
    setFile(null);
    setResult(null);
    setError(null);
    onClose();
  };

  const handleImport = async () => {
  if (!file) return;

  if (!manager?.id) {
    setError("Sales Manager ID is missing.");
    return;
  }

  try {
    setLoading(true);
    setError(null);

    const res = await importLeadsFromExcel({
      file,
      salesManagerId: manager.id,
    });

    if (res.success) {
      setResult(res);
      if (onSuccess) onSuccess(manager.id, res.importedCount);
    } else {
      setError(res.message || "Import failed. Please try again.");
    }
  } catch (err) {
    setError(
      err?.response?.data?.message || "Something went wrong. Please try again."
    );
  } finally {
    setLoading(false);
  }
};

  const ResultRow = ({ label, value, color }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 1.2,
        borderBottom: "1px solid #f1f5f9",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography fontSize={13} color="#6b7280">
        {label}
      </Typography>
      <Typography fontSize={13} fontWeight={600} color={color || "#111827"}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 3 }}>
        {/* ── Header ── */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2.5 }}>
          <Box>
            <Typography fontWeight={600} fontSize={18} color="#111827">
              Import Leads From Excel
            </Typography>
            <Typography fontSize={13} color="#6b7280" mt={0.3}>
              Importing leads for <b>{manager?.name}</b>
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ── UPLOAD PHASE ── */}
        {!result && (
          <>
            {/* Drop Zone */}
            <Box
              onClick={() => !loading && fileInputRef.current?.click()}
              sx={{
                border: "2px dashed",
                borderColor: file ? "#16a34a" : "#d1d5db",
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                cursor: loading ? "not-allowed" : "pointer",
                bgcolor: file ? "#f0fdf4" : "#f9fafb",
                transition: "all 0.2s",
                "&:hover": !loading
                  ? { borderColor: "#2563eb", bgcolor: "#eff6ff" }
                  : {},
              }}
            >
              <UploadFileIcon
                sx={{ fontSize: 40, color: file ? "#16a34a" : "#9ca3af", mb: 1 }}
              />
              {file ? (
                <>
                  <Typography fontSize={14} fontWeight={500} color="#16a34a">
                    {file.name}
                  </Typography>
                  <Typography fontSize={12} color="#6b7280" mt={0.5}>
                    Click to change file
                  </Typography>
                </>
              ) : (
                <>
                  <Typography fontSize={14} fontWeight={500} color="#374151">
                    Click to upload Excel file
                  </Typography>
                  <Typography fontSize={12} color="#9ca3af" mt={0.5}>
                    Supported formats: .xlsx, .xls
                  </Typography>
                </>
              )}
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {/* Column hint */}
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                bgcolor: "#eff6ff",
                borderRadius: 1.5,
                border: "1px solid #bfdbfe",
              }}
            >
              <Typography fontSize={12} fontWeight={600} color="#1d4ed8" mb={0.3}>
                Expected Excel columns
              </Typography>
              <Typography fontSize={12} color="#4b5563">
                <b>name</b> &nbsp;|&nbsp; <b>contactNumber</b> &nbsp;|&nbsp;{" "}
                <b>email</b> (optional)
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2, fontSize: 13 }}>
                {error}
              </Alert>
            )}

            {/* Footer */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={loading}
                sx={{ textTransform: "none", borderRadius: "8px" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={!file || loading}
                sx={{
                  textTransform: "none",
                  bgcolor: "#2563eb",
                  borderRadius: "8px",
                  minWidth: 110,
                  "&:hover": { bgcolor: "#1d4ed8" },
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={15} sx={{ color: "#fff" }} />
                    Importing...
                  </Box>
                ) : (
                  "Import"
                )}
              </Button>
            </Box>
          </>
        )}

        {/* ── RESULT PHASE ── */}
        {result && (
          <>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 52, color: "#16a34a" }} />
              <Typography fontSize={16} fontWeight={600} color="#111827" mt={1}>
                Import Complete
              </Typography>
            </Box>

            <Box
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                px: 2,
                py: 0.5,
                mb: 2,
              }}
            >
              <ResultRow label="Total rows in Excel" value={result.totalRows} />
              <ResultRow
                label="Successfully imported"
                value={result.importedCount}
                color="#16a34a"
              />
              <ResultRow
                label="Duplicates skipped"
                value={result.duplicateCount}
                color="#f59e0b"
              />
              <ResultRow
                label="Failed rows"
                value={result.failedCount}
                color={result.failedCount > 0 ? "#dc2626" : "#6b7280"}
              />
            </Box>

            {result.errors?.length > 0 && (
              <Box
                sx={{
                  bgcolor: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 1.5,
                  p: 1.5,
                  mb: 2,
                  maxHeight: 120,
                  overflowY: "auto",
                }}
              >
                <Typography fontSize={12} fontWeight={600} color="#dc2626" mb={0.5}>
                  Row errors:
                </Typography>
                {result.errors.slice(0, 5).map((err, i) => (
                  <Typography key={i} fontSize={12} color="#dc2626">
                    • {err}
                  </Typography>
                ))}
                {result.errors.length > 5 && (
                  <Typography fontSize={12} color="#dc2626">
                    ...and {result.errors.length - 5} more
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  textTransform: "none",
                  bgcolor: "#2563eb",
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#1d4ed8" },
                }}
              >
                Done
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
